import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowRight, Plus, Settings, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Columns to auto-skip during upload (case-insensitive partial matching)
const COLUMNS_TO_SKIP = [
  'patient last',
  'patient first',
  'medical record',
  'branch name',
  'phone',
  'time in',
  'time out',
  'documentation time',
  'travel time',
  'productivity units',
  'mileage',
  'employee id'
];

export default function Upload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dupCheckResult, setDupCheckResult] = useState(null);
  const [excludedRows, setExcludedRows] = useState(new Set());
  const [showMapping, setShowMapping] = useState(false);
  const [columns, setColumns] = useState([]);
  const [mappings, setMappings] = useState({});
  const [parsedRows, setParsedRows] = useState(null); // Store parsed data from first read
  const fileInputRef = useRef(null);

  // Custom field creation modal
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [fieldModalColumn, setFieldModalColumn] = useState('');
  const [newField, setNewField] = useState({
    name: '', field_type: 'text', target_page: 'personnel', target_tab: 'personal',
    target_section: '', description: ''
  });
  const [createdFields, setCreatedFields] = useState({}); // columnName -> custom field key

  const PERSONNEL_TABS = [
    { value: 'personal', label: 'Personal' },
    { value: 'job', label: 'Job' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'benefits', label: 'Benefits' },
    { value: 'pay', label: 'Pay' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'documents', label: 'Documents' },
  ];

  const AGENCY_TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'contacts', label: 'Contacts' },
    { value: 'details', label: 'Details' },
  ];

  const openFieldModal = (columnName) => {
    setFieldModalColumn(columnName);
    setNewField({
      name: columnName, field_type: 'text', target_page: 'personnel',
      target_tab: 'personal', target_section: '', description: ''
    });
    setShowFieldModal(true);
  };

  const handleCreateField = async () => {
    if (!newField.name) { toast.error('Field name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/custom-fields`, {
        ...newField,
        source: 'upload',
        source_column: fieldModalColumn
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const fieldKey = res.data.field_key;
      // Map this column to the custom field
      setCreatedFields(prev => ({ ...prev, [fieldModalColumn]: fieldKey }));
      setMappings(prev => ({ ...prev, [fieldModalColumn]: `custom:${fieldKey}` }));
      setShowFieldModal(false);
      toast.success(`Field "${newField.name}" created and mapped`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to create field');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(selectedFile);
        setUploadResult(null);
        readExcelColumns(selectedFile);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (
        droppedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        droppedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(droppedFile);
        setUploadResult(null);
        readExcelColumns(droppedFile);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const readExcelColumns = (file) => {
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error('Failed to read the file. Please try selecting it again.');
    };
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const headerData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        // Also parse rows with headers for upload use later
        const rowsWithHeaders = XLSX.utils.sheet_to_json(firstSheet);
        setParsedRows(rowsWithHeaders);
        
        if (headerData.length > 0) {
          const headers = headerData[0];
          setColumns(headers);
          
          // Auto-map common column names
          const autoMappings = {};
          headers.forEach((header) => {
            const lowerHeader = header.toLowerCase().trim();
            const normalized = lowerHeader.replace(/[_\s]+/g, ' ');
            
            // Check if this column should be auto-skipped (partial match)
            const shouldSkip = COLUMNS_TO_SKIP.some(skipCol => 
              normalized.startsWith(skipCol) || normalized === skipCol
            );
            
            if (shouldSkip) {
              autoMappings[header] = 'skip';
            } else if (
              // Match: "Provider First", "First Name", "Therapist First Name", etc.
              (lowerHeader.includes('first') && !lowerHeader.includes('patient')) &&
              (lowerHeader.includes('provider') || lowerHeader.includes('therapist') || lowerHeader.includes('name'))
            ) {
              autoMappings[header] = 'first_name';
            } else if (
              (lowerHeader.includes('last') && !lowerHeader.includes('patient')) &&
              (lowerHeader.includes('provider') || lowerHeader.includes('therapist') || lowerHeader.includes('name'))
            ) {
              autoMappings[header] = 'last_name';
            } else if (lowerHeader.includes('date')) {
              autoMappings[header] = 'date';
            } else if (lowerHeader.includes('task') && lowerHeader.includes('name')) {
              autoMappings[header] = 'task_name';
            } else if (lowerHeader.includes('task') && lowerHeader.includes('type')) {
              autoMappings[header] = 'task_type';
            } else if (lowerHeader.includes('status')) {
              autoMappings[header] = 'status';
            } else if (lowerHeader.includes('address')) {
              autoMappings[header] = 'address';
            } else if (lowerHeader.includes('city')) {
              autoMappings[header] = 'city';
            } else if (lowerHeader.includes('state')) {
              autoMappings[header] = 'state';
            } else if (lowerHeader.includes('zip')) {
              autoMappings[header] = 'zip_code';
            } else if (lowerHeader.includes('agency') || lowerHeader.includes('insurance')) {
              autoMappings[header] = 'agency';
            } else {
              autoMappings[header] = 'additional';
            }
          });
          
          setMappings(autoMappings);
          setShowMapping(true);
        }
      } catch (error) {
        toast.error('Failed to read Excel file. Please check the file format.');
        console.error('Excel read error:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const buildTransformedData = () => {
    return parsedRows
      .filter(row => Object.values(row).some(v => v !== null && v !== undefined && v !== ''))
      .map((row) => {
        const newRow = {};
        Object.entries(row).forEach(([key, value]) => {
          const mapping = mappings[key];
          if (mapping && mapping !== 'skip') {
            if (mapping.startsWith('custom:')) {
              const fieldKey = mapping.replace('custom:', '');
              if (!newRow.custom_data) newRow.custom_data = {};
              newRow.custom_data[fieldKey] = value !== null && value !== undefined ? String(value) : '';
            } else if (mapping === 'additional') {
              if (!newRow.additional_data) newRow.additional_data = {};
              newRow.additional_data[key] = value;
            } else {
              newRow[mapping] = value !== null && value !== undefined ? String(value) : '';
            }
          }
        });
        return newRow;
      })
      .filter(row => row.first_name || row.last_name);
  };

  const handleCheckDuplicates = async () => {
    if (!file || !parsedRows || parsedRows.length === 0) return;
    const hasFirstName = Object.values(mappings).includes('first_name');
    const hasLastName = Object.values(mappings).includes('last_name');
    if (!hasFirstName || !hasLastName) { toast.error('Please map First Name and Last Name columns'); return; }

    setChecking(true);
    try {
      const transformedData = buildTransformedData();
      if (transformedData.length === 0) { toast.error('No valid rows found'); setChecking(false); return; }

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload-mapped/check-duplicates`, { data: transformedData }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 60000,
      });
      setDupCheckResult({ ...res.data, transformedData });
      setExcludedRows(new Set());
      if (res.data.duplicates_existing > 0 || res.data.duplicates_batch > 0) {
        toast.warning(`Found ${res.data.duplicates_existing} existing + ${res.data.duplicates_batch} batch duplicates`);
      } else {
        toast.success('No duplicates found');
      }
    } catch (err) {
      toast.error('Duplicate check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (!parsedRows || parsedRows.length === 0) {
      toast.error('No data found in the file. Please re-select the file.');
      return;
    }

    const hasFirstName = Object.values(mappings).includes('first_name');
    const hasLastName = Object.values(mappings).includes('last_name');
    if (!hasFirstName || !hasLastName) {
      toast.error('Please map First Name and Last Name columns');
      return;
    }

    setUploading(true);
    
    try {
      // Use pre-checked data if available, otherwise build fresh
      let transformedData;
      if (dupCheckResult && dupCheckResult.transformedData) {
        // Filter out excluded rows
        transformedData = dupCheckResult.transformedData.filter((_, idx) => !excludedRows.has(idx));
      } else {
        transformedData = buildTransformedData();
      }

      if (transformedData.length === 0) {
        toast.error('No valid rows found in the file. Check that names are present.');
        setUploading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload-mapped`, {
        data: transformedData
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 120000
      });
      
      setUploadResult(response.data);
      toast.success('File uploaded successfully!');
      setFile(null);
      setParsedRows(null);
      setShowMapping(false);
      setColumns([]);
      setMappings({});
      setDupCheckResult(null);
      setExcludedRows(new Set());
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (detail) {
        toast.error(`Upload error: ${detail}`);
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Upload timed out. Try uploading a smaller file or check your connection.');
      } else if (status === 413) {
        toast.error('File too large. Try splitting into smaller files.');
      } else if (!error.response) {
        toast.error(`Network error: ${error.message}. Check your connection and try again.`);
      } else {
        toast.error(`Upload failed (${status || 'unknown'}): ${error.message}`);
      }
      console.error('Upload error details:', {
        status,
        detail,
        message: error.message,
        response: error.response?.data
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
          Upload Data
        </h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Upload Excel files to add or update visit records
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Upload Excel File</h3>
          
          {!showMapping ? (
            <>
              <div
                className={`drag-drop-zone rounded-xl p-12 text-center cursor-pointer ${
                  dragOver ? 'drag-over' : ''
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <FileSpreadsheet className="mx-auto mb-4 text-primary" size={48} strokeWidth={1.5} />
                <p className="text-foreground font-medium mb-2">
                  Drag and drop your Excel file here
                </p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  Supported formats: .xlsx, .xls
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />

              {file && !showMapping && (
                <div className="mt-6 p-4 bg-muted rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="text-primary" size={24} />
                    <div>
                      <p className="font-medium text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-sm">{file.name}</p>
                  <p className="text-xs text-muted-foreground">Map your columns</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowMapping(false);
                    setFile(null);
                    setParsedRows(null);
                    setColumns([]);
                    setMappings({});
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  data-testid="cancel-mapping"
                >
                  Cancel
                </Button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-3 border border-border rounded-lg p-4">
                {columns.map((column, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Excel Column</Label>
                      <p className="font-medium text-sm mt-1">{column}</p>
                    </div>
                    <ArrowRight className="text-muted-foreground flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Map To</Label>
                      <Select
                        value={mappings[column] || 'additional'}
                        onValueChange={(value) => {
                          if (value === 'create_new') {
                            openFieldModal(column);
                          } else {
                            setMappings({ ...mappings, [column]: value });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid={`map-${column}`}>
                          <SelectValue>
                            {mappings[column]?.startsWith('custom:') 
                              ? `Custom: ${createdFields[column] || mappings[column].replace('custom:', '')}`
                              : undefined
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first_name">First Name</SelectItem>
                          <SelectItem value="last_name">Last Name</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="task_name">Task Name</SelectItem>
                          <SelectItem value="task_type">Task Type</SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="address">Address</SelectItem>
                          <SelectItem value="city">City</SelectItem>
                          <SelectItem value="state">State</SelectItem>
                          <SelectItem value="zip_code">ZIP Code</SelectItem>
                          <SelectItem value="agency">Agency</SelectItem>
                          <SelectItem value="additional">Additional Data</SelectItem>
                          <SelectItem value="skip">Skip Column</SelectItem>
                          <SelectItem value="create_new" className="text-primary font-medium border-t">
                            <span className="flex items-center gap-1"><Plus size={12} /> Create New Field</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {Object.values(mappings).filter(v => v === 'first_name').length > 0 && 
                   Object.values(mappings).filter(v => v === 'last_name').length > 0 ? (
                    <span className="text-sage flex items-center gap-2">
                      <CheckCircle2 size={16} />
                      Required fields mapped
                    </span>
                  ) : (
                    <span className="text-clay flex items-center gap-2">
                      <AlertCircle size={16} />
                      Map First Name and Last Name
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCheckDuplicates}
                    disabled={uploading || checking}
                    variant="outline"
                    className="gap-2"
                    data-testid="check-duplicates-button"
                  >
                    <AlertTriangle size={16} />
                    {checking ? 'Checking...' : 'Check Duplicates'}
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="gap-2"
                    data-testid="upload-button"
                  >
                    <UploadIcon size={16} />
                    {uploading ? 'Uploading...' : (dupCheckResult ? `Upload ${dupCheckResult.transformedData.length - excludedRows.size} Rows` : 'Upload Data')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Duplicate Check Results */}
        {dupCheckResult && (dupCheckResult.duplicates_existing > 0 || dupCheckResult.duplicates_batch > 0) && (
          <Card className="p-6 border-amber-200 bg-amber-50/50" data-testid="duplicate-results">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} className="text-amber-600" />
              <h3 className="font-heading text-lg font-semibold text-amber-800">
                Duplicates Found: {dupCheckResult.duplicates_existing} existing, {dupCheckResult.duplicates_batch} in-batch
              </h3>
            </div>
            <p className="text-sm text-amber-700 mb-4">
              Uncheck rows you want to exclude from the upload. Checked rows will be uploaded.
            </p>
            <div className="max-h-[400px] overflow-y-auto border rounded bg-white">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-amber-100">
                  <tr>
                    <th className="p-2 text-left w-10">
                      <input type="checkbox" checked={excludedRows.size === 0}
                        onChange={e => {
                          if (e.target.checked) setExcludedRows(new Set());
                          else {
                            const allDupIdx = new Set(dupCheckResult.results.filter(r => r.duplicate_type).map(r => r.row_index));
                            setExcludedRows(allDupIdx);
                          }
                        }} />
                    </th>
                    <th className="p-2 text-left">Row</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Duplicate Type</th>
                    <th className="p-2 text-left">Match Details</th>
                  </tr>
                </thead>
                <tbody>
                  {dupCheckResult.results.filter(r => r.duplicate_type).map((r, i) => (
                    <tr key={i} className={`border-b ${excludedRows.has(r.row_index) ? 'bg-red-50 opacity-60' : 'bg-amber-50/30'}`}>
                      <td className="p-2">
                        <input type="checkbox" checked={!excludedRows.has(r.row_index)}
                          onChange={e => {
                            const next = new Set(excludedRows);
                            if (e.target.checked) next.delete(r.row_index);
                            else next.add(r.row_index);
                            setExcludedRows(next);
                          }} />
                      </td>
                      <td className="p-2 text-muted-foreground">{r.row_index + 1}</td>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2">
                        <Badge className={r.duplicate_type === 'existing' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                          {r.duplicate_type === 'existing' ? 'Exists in System' : 'Batch Duplicate'}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {r.matches.map((m, mi) => (
                          <span key={mi}>
                            {m.name} {m.status && `(${m.status})`} {m.discipline && `- ${m.discipline}`} {m.note || ''}
                            {mi < r.matches.length - 1 && ', '}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              {excludedRows.size > 0 ? `${excludedRows.size} row(s) will be excluded from upload.` : 'All rows will be uploaded.'}
            </p>
          </Card>
        )}


        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Excel Format Guidelines</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Required Columns</p>
                <p className="text-sm text-muted-foreground">
                  Provider First/Last or First Name/Last Name
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Optional Columns</p>
                <p className="text-sm text-muted-foreground">
                  Date, Task Name, Task Type, Status, Address, City, State, ZIP Code, Agency
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <AlertCircle className="text-clay flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Important Notes</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>First row should contain column headers</li>
                  <li>Profiles are matched by first and last name</li>
                  <li>Use the mapping interface to match your columns</li>
                  <li>Additional columns will be stored as extra data</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {uploadResult && (
        <Card className="p-6 bg-sage/5 border-sage/20">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="text-sage flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-sage mb-3">
                Upload Successful!
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Profiles Created</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.profiles_created}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Profiles Updated</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.profiles_updated}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Visits Added</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.visits_added}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Agencies</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {uploadResult.insurance_profiles_created}
                  </p>
                </div>
                {uploadResult.personnel_created > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">New Therapists</p>
                    <p className="text-2xl font-bold text-primary tabular-nums">
                      {uploadResult.personnel_created}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Agency Breakdown */}
              {uploadResult.agency_breakdown && Object.keys(uploadResult.agency_breakdown).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <FileSpreadsheet size={16} className="text-primary" />
                    Agency Breakdown (for debugging)
                  </h4>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Agency</th>
                          <th className="text-center p-2 font-medium">Raw Count</th>
                          <th className="text-center p-2 font-medium">Weighted</th>
                          <th className="text-left p-2 font-medium">Visit Types</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(uploadResult.agency_breakdown)
                          .sort((a, b) => b[1].weighted_count - a[1].weighted_count)
                          .map(([agency, data]) => (
                          <tr key={agency} className="border-t hover:bg-muted/30">
                            <td className="p-2 font-medium">{agency}</td>
                            <td className="p-2 text-center">{data.count}</td>
                            <td className="p-2 text-center font-bold text-primary">{data.weighted_count}</td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(data.visit_types || {}).map(([type, info]) => (
                                  <span 
                                    key={type} 
                                    className="inline-flex items-center px-2 py-0.5 bg-muted rounded text-xs"
                                    title={`${info.count} visits, ${info.weighted} weighted`}
                                  >
                                    {type}: {info.count} ({info.weighted}w)
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    * "Weighted" applies 2x multiplier for billable OASIS visits. Use this to compare against your expected totals.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>

    {/* Create New Field Modal */}
    <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
      <DialogContent className="sm:max-w-lg" data-testid="create-field-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings size={18} className="text-primary" />
            Create New Field
          </DialogTitle>
          <DialogDescription>
            Create a custom field for the column "{fieldModalColumn}" and choose where it should appear.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Field Name *</Label>
            <Input
              data-testid="new-field-name"
              value={newField.name}
              onChange={e => setNewField({ ...newField, name: e.target.value })}
              placeholder="e.g., Life Insurance License Number"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Field Type</Label>
              <Select value={newField.field_type} onValueChange={v => setNewField({ ...newField, field_type: v })}>
                <SelectTrigger data-testid="new-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Display On</Label>
              <Select value={newField.target_page} onValueChange={v => setNewField({ ...newField, target_page: v, target_tab: v === 'personnel' ? 'personal' : v === 'agency' ? 'overview' : '' })}>
                <SelectTrigger data-testid="new-field-page"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="personnel">Personnel Page</SelectItem>
                  <SelectItem value="agency">Agency Page</SelectItem>
                  <SelectItem value="reports_only">Reports Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {newField.target_page !== 'reports_only' && (
            <div>
              <Label>Tab</Label>
              <Select value={newField.target_tab} onValueChange={v => setNewField({ ...newField, target_tab: v })}>
                <SelectTrigger data-testid="new-field-tab"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(newField.target_page === 'personnel' ? PERSONNEL_TABS : AGENCY_TABS).map(tab => (
                    <SelectItem key={tab.value} value={tab.value}>{tab.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Description (optional)</Label>
            <Input
              value={newField.description}
              onChange={e => setNewField({ ...newField, description: e.target.value })}
              placeholder="Brief description of what this field stores"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowFieldModal(false)}>Cancel</Button>
          <Button onClick={handleCreateField} data-testid="create-field-submit">
            <Plus size={14} className="mr-2" /> Create Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}